import React, { useState, useEffect, useMemo } from 'react';
import './Backgrounds.css';

const Backgrounds = () => {
    const [backgroundImage, setBackgroundImage] = useState('');
    const [customBackground, setCustomBackground] = useState('');
    const [name, setName] = useState('');
    const [position, setPosition] = useState('');
    const [companyName, setCompanyName] = useState('');

    // Memoize the stockImages array
    const stockImages = useMemo(() => [
        'stock1.jpeg', // Add the paths to your stock images here
        'stock2.jpeg',
        'stock3.jpeg',
        'stock4.jpeg',
        'stock5.jpeg',
        'stock6.jpeg',
        'stock7.jpeg',
        'stock8.jpeg',
        'stock9.jpeg',
    ], []);

    useEffect(() => {
        // Set the first stock image as the default background
        if (stockImages.length > 0) {
            setBackgroundImage(stockImages[0]);
        }
    }, [stockImages]);

    const handleCustomBackgroundUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setCustomBackground(reader.result);
                setBackgroundImage(''); // Clear the stock background when a custom background is uploaded
            };
            reader.readAsDataURL(file);
        }
    };

    const handleBackgroundDownload = () => {
        // Create an off-screen canvas (1920x1080 Full HD)
        const canvas = document.createElement('canvas');
        canvas.width = 1920;
        canvas.height = 1080;
        const ctx = canvas.getContext('2d');

        // Draw the background image
        const img = new Image();
        img.src = customBackground || backgroundImage;
        
        img.onload = () => {
            // 1. Draw background image
            ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

            // 2. Configure elegant drop shadows to match the CSS text-shadow rule
            ctx.shadowColor = 'rgba(0, 0, 0, 0.4)';
            ctx.shadowBlur = 12;
            ctx.shadowOffsetX = 0;
            ctx.shadowOffsetY = 6;

            // 3. Draw the Name (Bold Montserrat - scaled up for 1080p layout)
            ctx.font = '700 96px Montserrat, sans-serif';
            ctx.fillStyle = '#ffffff';
            ctx.textAlign = 'left';
            ctx.fillText(name || 'Your Name', 120, 150); // Scaled padding and height

            // 4. Draw Sub-details (Regular Montserrat)
            ctx.font = '400 52px Montserrat, sans-serif';
            
            // Remove shadow briefly if you want cleaner sub-text, or keep it for readability
            ctx.fillText(position || 'Your Position', 120, 240); 
            ctx.fillText(companyName || 'Your Company', 120, 320);

            // 5. Draw the Premium Pill-Style Watermark (Bottom Right)
            // Reset text shadow configuration for the clean UI badge container
            ctx.shadowColor = 'transparent';
            ctx.shadowBlur = 0;
            ctx.shadowOffsetX = 0;
            ctx.shadowOffsetY = 0;

            const watermarkText = 'Created with Digicard.com';
            ctx.font = '500 26px Montserrat, sans-serif';
            
            // Calculate dynamic background capsule width based on the text size
            const textWidth = ctx.measureText(watermarkText).width;
            const paddingX = 36;
            const paddingY = 18;
            const badgeWidth = textWidth + (paddingX * 2);
            const badgeHeight = 26 + (paddingY * 2);
            
            const badgeX = canvas.width - 120 - badgeWidth;
            const badgeY = canvas.height - 90 - badgeHeight;

            // Draw translucent container pill match the CSS backdrop style
            ctx.fillStyle = 'rgba(0, 0, 0, 0.25)';
            // Draw a rounded rectangle for the badge container
            ctx.beginPath();
            ctx.roundRect ? ctx.roundRect(badgeX, badgeY, badgeWidth, badgeHeight, 50) : ctx.rect(badgeX, badgeY, badgeWidth, badgeHeight);
            ctx.fill();

            // Overlay text onto the container badge
            ctx.fillStyle = 'rgba(255, 255, 255, 0.95)';
            ctx.textAlign = 'left';
            ctx.fillText(watermarkText, badgeX + paddingX, badgeY + paddingY + 22);

            // 6. Trigger Download Action
            const link = document.createElement('a');
            link.download = 'digicard-background.png';
            link.href = canvas.toDataURL('image/png');
            link.click();
        };
    };

    const handleStockImageClick = (image) => {
        setBackgroundImage(image);
        setCustomBackground(''); // Clear the custom background if a stock image is chosen
    };

    return (
        <div className="backgrounds-container">
            {/* Add the Backgrounds title */}
            <h1 className="backgrounds-title">Backgrounds</h1>

            <div className="main-content">
                {/* Background Preview */}
                <div className="background-preview-container">
                    <div
                        className="background-preview"
                        style={{
                            backgroundImage: `url(${customBackground || backgroundImage})`,
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                            width: '640px',
                            height: '360px',
                            position: 'relative',
                            zIndex: '1', // Ensure it is below the side menu
                        }}
                    >
                        <div className="text-overlay">
                            <h2>{name}</h2>
                            <p>{position}</p>
                            <p>{companyName}</p>
                        </div>
                        {/* Watermark for Preview */}
                        <div className="watermark">
                            Created with Digicard.com
                        </div>
                    </div>
                    <button className="download-button" onClick={handleBackgroundDownload}>Download Background</button>
                </div>

                {/* Input Section Moved to the Right */}
                <div className="input-section">
                    <label>
                        Name:
                        <input
                            type="text"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                        />
                    </label>
                    <label>
                        Position:
                        <input
                            type="text"
                            value={position}
                            onChange={(e) => setPosition(e.target.value)}
                        />
                    </label>
                    <label>
                        Company Name:
                        <input
                            type="text"
                            value={companyName}
                            onChange={(e) => setCompanyName(e.target.value)}
                        />
                    </label>
                </div>
            </div>

            <div className="upload-section">
                <h3>Upload Your Own Background</h3>
                <input type="file" onChange={handleCustomBackgroundUpload} />
            </div>

            <div className="stock-images-section">
                <h3>Choose a Stock Background</h3>
                <div className="stock-images">
                    {stockImages.map((image, index) => (
                        <img
                            key={index}
                            src={image}
                            alt={`Stock ${index}`}
                            className={`stock-image ${backgroundImage === image ? 'selected' : ''}`}
                            onClick={() => handleStockImageClick(image)}
                        />
                    ))}
                </div>
            </div>
        </div>
    );
};

export default Backgrounds;
